import { ReactNode, forwardRef, useState, useEffect, useRef, Ref } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import * as CommonIcons from 'assets/common';
import { classNames, getDataPropsFromRest } from 'utilities/Utils';
import { StyledEngineProvider } from '@mui/material';
import { useResizeObserver } from 'utilities/CustomHooks';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  slider: {
    width: 'inherit',
  },
  slides: {
    width: 'inherit',
    display: 'flex',
    overflow: 'hidden',
  },
  slide: {
    flexShrink: 0,
    width: 'inherit',
    borderRadius: '4px',
  },
  nextPrev: {
    cursor: 'pointer',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  left: {
    left: '-32px',
    zIndex: 1, //not affecting current component
  },
  right: {
    right: '-32px',
  },
}));

enum Direction {
  LEFT = 'left',
  RIGHT = 'right',
}

type CarouselProps = {
  children: ReactNode | ReactNode[];
  showScrollIcon?: boolean;
  onLeftClick?: Function;
  onRightClick?: Function;
  itemClass?: string;
  customButtons?: {
    left: ReactNode | ReactNode[];
    right: ReactNode | ReactNode[];
    leftClass?: string;
    rightClass?: string;
  };
  className?: string;
  showBothButtons?: boolean;
  shiftItems?: number;
};

const MultiItemCarouselComp = (
  props: CarouselProps,
  ref?: Ref<HTMLDivElement>,
) => {
  const {
    children,
    showScrollIcon = true,
    onLeftClick = () => {},
    onRightClick = () => {},
    itemClass = '', //className for items
    customButtons = null, //adding custom btns, existing are diff from design,
    className = '',
    showBothButtons = false,
    shiftItems = 1,
    ...rest
  } = props;

  const dataProps = getDataPropsFromRest(rest);

  const [activeItem, setActiveItem] = useState(0);
  const [isDisabled, setIsDisabled] = useState({ left: true, right: true });

  const sliderRef = useRef<HTMLDivElement>();
  const childRefs = useRef<HTMLDivElement[]>([]);

  const { width } = useResizeObserver(sliderRef, true);

  const classes = useStyles();

  const shift = (dir: Direction) => {
    if (dir === Direction.LEFT && !isDisabled.left) {
      setActiveItem(i => (i - shiftItems >= 0 ? i - shiftItems : 0));
      onLeftClick();
    } else if (dir === Direction.RIGHT && !isDisabled.right) {
      setActiveItem(i =>
        i + shiftItems < (children as ReactNode[]).length
          ? i + shiftItems
          : (children as ReactNode[]).length - 1,
      );
      onRightClick();
    }
  };

  const getScrollParameters = (slider, nextChild, lastChild) => {
    const parentRect = slider.getBoundingClientRect();
    const nextChildRect = nextChild.getBoundingClientRect();
    const lastChildRect = lastChild.getBoundingClientRect();
    const shiftBy = nextChildRect.x - parentRect.x;
    const isRightDisabled = lastChildRect.right - shiftBy <= parentRect.right;
    return { shiftBy, isRightDisabled };
  };

  useEffect(() => {
    const { shiftBy, isRightDisabled } = getScrollParameters(
      sliderRef.current,
      childRefs.current[activeItem],
      childRefs.current[(children as ReactNode[]).length - 1],
    );
    setIsDisabled({
      left: activeItem <= 0,
      right: isRightDisabled,
    });
    sliderRef.current.scrollBy({
      left: shiftBy,
      behavior: 'smooth',
    });
  }, [activeItem, width]);

  return (
    <StyledEngineProvider injectFirst>
      <div ref={ref} className={classes.root} {...dataProps}>
        {showScrollIcon && (showBothButtons || activeItem > 0) && (
          <div
            className={classNames(
              classes.nextPrev,
              classes.left,
              customButtons?.leftClass,
              'select-none',
              isDisabled.left ? 'cursor-not-allowed' : '',
            )}
            onClick={() => {
              shift(Direction.LEFT);
            }}
          >
            {customButtons ? customButtons.left : <CommonIcons.ChevronLeft />}
          </div>
        )}
        <div className={classes.slider}>
          <div
            className={classNames(classes.slides, className)}
            ref={sliderRef}
          >
            {Array.isArray(children) ? (
              children.map((child, index) => (
                <div
                  className={classNames(classes.slide, itemClass)}
                  key={`slide_${index}`}
                  id={`slide_${index}`}
                  ref={el => (childRefs.current[index] = el)}
                >
                  {child}
                </div>
              ))
            ) : (
              <div className={classes.slide}>{children}</div>
            )}
          </div>
        </div>
        {showScrollIcon &&
          Array.isArray(children) &&
          (showBothButtons || activeItem !== children.length - 1) && (
            <div
              className={classNames(
                classes.nextPrev,
                classes.right,
                customButtons?.rightClass,
                'select-none',
                isDisabled.right ? 'cursor-not-allowed' : '',
              )}
              onClick={() => {
                shift(Direction.RIGHT);
              }}
            >
              {customButtons ? (
                customButtons.right
              ) : (
                <CommonIcons.ChevronRight />
              )}
            </div>
        )}
      </div>
    </StyledEngineProvider>
  );
};

const MultiItemCarousel = forwardRef(MultiItemCarouselComp);
export { MultiItemCarousel };
